import FacebookLogin from '@greatsumini/react-facebook-login';
import { Button, ButtonProps } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'tabler-icons-react';
import axios from '../../api/axios';
import { FacebookRegistrationStatus } from '../../enums/facebook-registration-status.enum';
import useAuth from '../../hooks/useAuth';
import { ErrorResponse } from '../../type/error-response.types';
import { FacebookIcon } from './facebook-icon.component';

export function FacebookButton(props: ButtonProps<'button'>) {
  const { setAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleFacebookLoginSuccess = async (response: any) => {
    axios
      .post(
        `/web/auth/signin/facebook?access_token=${response.accessToken}`,
        null,
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        const { registrationStatus } = res.data;

        if (
          registrationStatus ===
          FacebookRegistrationStatus.INCOMPLETE_REGISTRATION
        ) {
          const { id: facebookId, firstName, lastName, user } = res.data;
          const { email } = user;

          setAuth({
            continueRegistration: {
              facebookId,
              firstName,
              lastName,
              email,
            },
          });

          // Don't redirect if already in '/register' page
          if (location.pathname !== '/register') {
            navigate('/register');
          }
        } else {
          const {
            id,
            type,
            user,
            accessToken,
            role,
            onboardingStatus,
            onboardingReferenceNumber,
          } = res.data;
          const { email, username } = user;

          setAuth({
            id,
            type,
            username,
            accessToken,
            email,
            role,
            onboardingStatus,
            onboardingReferenceNumber,
          });

          navigate('/dashboard');
        }
      })
      .catch((err) => {
        const error: ErrorResponse = err.response?.data;

        let message = error.error || 'An Unexpected Error Occurred';
        showNotification({
          id: 'failed-to-login-with-facebook',
          message: message,
          icon: <X size={16} />,
          color: 'red',
          autoClose: 6000,
        });
      })
      .finally(() => {});
  };

  const handleFacebookLoginFailed = (error: { status: string }) => {
    if (error.status !== 'loginCancelled') {
      showNotification({
        id: 'failed-to-login-with-facebook2',
        message: error.status || 'Failed to login with facebook',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 6000,
      });
    }
  };

  return (
    <FacebookLogin
      appId={process.env.REACT_APP_FACEBOOK_APP_ID || ''}
      onSuccess={handleFacebookLoginSuccess}
      onFail={(error) => handleFacebookLoginFailed(error)}
      render={(renderProps) => (
        <Button
          leftIcon={<FacebookIcon />}
          sx={(theme) => ({
            width: '100%',
            backgroundColor: '#4267B2',
            color: '#fff',
            '&:hover': {
              backgroundColor: theme.fn.darken('#4267B2', 0.1),
            },
          })}
          {...props}
          onClick={renderProps.onClick}
        >
          {location.pathname === '/login'
            ? 'Sign in with Facebook'
            : 'Sign up with Facebook'}
        </Button>
      )}
    />
  );
}
